import React, { useEffect } from 'react';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  /** A string, or a heading with a badge beside it. */
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
  isSubmitting?: boolean;
  titleId?: string;
  /** Escape closes the dialog. Default true. */
  closeOnEscape?: boolean;
  /** A click on the dimmed canvas closes the dialog. Default true. */
  closeOnScrim?: boolean;
  /** Show the × in the title bar. Default true. */
  showClose?: boolean;
  /** Cap the body's height and scroll it, keeping the title and footer in view. */
  scrollBody?: boolean;
}

/**
 * The app's dialog shell: scrim, card, title with a close control, body and an
 * optional footer for the kit Buttons. Escape and a scrim click close it,
 * except while `isSubmitting`. Use it instead of a hand-built overlay.
 */
const Modal: React.FC<ModalProps> = ({
  open,
  title,
  onClose,
  children,
  footer,
  maxWidth = 520,
  isSubmitting = false,
  titleId = 'modal-title',
  closeOnEscape = true,
  closeOnScrim = true,
  showClose = true,
  scrollBody = false,
}) => {
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, isSubmitting, onClose, closeOnEscape]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={isSubmitting || !closeOnScrim ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className={styles.content}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 id={titleId} className={styles.title}>{title}</h3>
          {showClose && (
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>

        {scrollBody ? <div className={styles.scrollBody}>{children}</div> : children}

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
